import { Box, Button, Checkbox, Collapse, FormControlLabel, IconButton, MenuItem, Switch, TextField, Typography } from '@mui/material'
import { ExpandLess, ExpandMore } from '@mui/icons-material'
import { useContext, useEffect, useState } from 'react'
import Context from '../Context'
import theme from '../theme'
import { DEFAULT_SETTINGS, leagueKey } from '../utils/settings'
import { logout } from '../utils/api'
import { ZONES } from '../utils/clock'

const fieldSx = {
  backgroundColor: 'white',
  borderRadius: '2px',
  width: '220px',
  '& .MuiOutlinedInput-input': { padding: '10px 12px', fontSize: '15px' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#C9C9D1' }
}

function SettingsPage() {
  const { settings, setSettings, knownLeagues, marketHost, username } = useContext(Context)
  const [ openSport, setOpenSport ] = useState(null)

  // edited locally and only committed on Save, so a half typed number never reaches the tables
  const [ draft, setDraft ] = useState(settings)
  const [ saved, setSaved ] = useState(false)

  useEffect(() => {
    setDraft(settings)
  }, [settings])

  const update = (key) => (event) => {
    setDraft({ ...draft, [key]: event.target.value })
    setSaved(false)
  }

  const toNumber = (value, fallback) => {
    const parsed = Number(value)
    return value === '' || Number.isNaN(parsed) ? fallback : parsed
  }

  const toggleSport = (sport) => (event) => {
    event.stopPropagation()
    const hidden = draft.hiddenSports || []
    setDraft({
      ...draft,
      hiddenSports: hidden.includes(sport) ? hidden.filter((s) => s !== sport) : [...hidden, sport]
    })
    setSaved(false)
  }

  const toggleLeague = (sport, league) => () => {
    const key = leagueKey(sport, league)
    const hidden = draft.hiddenLeagues || []
    setDraft({
      ...draft,
      hiddenLeagues: hidden.includes(key) ? hidden.filter((k) => k !== key) : [...hidden, key]
    })
    setSaved(false)
  }

  // every league of one sport at once, because ticking thirty boxes by hand is not a feature
  const setWholeSport = (sport, leagues, hide) => (event) => {
    event.stopPropagation()
    const keys = leagues.map((entry) => leagueKey(sport, entry.league))
    const hidden = (draft.hiddenLeagues || []).filter((k) => !keys.includes(k))
    setDraft({ ...draft, hiddenLeagues: hide ? [...hidden, ...keys] : hidden })
    setSaved(false)
  }

  const toggleCounts = () => {
    setDraft({ ...draft, showCounts: !draft.showCounts })
    setSaved(false)
  }

  const handleSave = () => {
    // spread, not replace: this screen owns five fields and the notification screen owns the rest.
    // Sending a bare literal dropped the others to undefined, and since everything downstream tests
    // `!== false`, one Save here silently switched the alert sound back on.
    setSettings({
      ...settings,
      minYield: toNumber(draft.minYield, DEFAULT_SETTINGS.minYield),
      minArbProfit: toNumber(draft.minArbProfit, DEFAULT_SETTINGS.minArbProfit),
      hiddenSports: draft.hiddenSports || [],
      hiddenLeagues: draft.hiddenLeagues || [],
      showCounts: Boolean(draft.showCounts),
      favoritesBoard: draft.favoritesBoard !== false,
      timeZone: draft.timeZone === 'local' ? 'local' : 'UTC'
    })
    setSaved(true)
  }

  const dirty = String(draft.minYield) !== String(settings.minYield)
    || String(draft.minArbProfit) !== String(settings.minArbProfit)
    || (draft.hiddenSports || []).join('~') !== (settings.hiddenSports || []).join('~')
    || (draft.hiddenLeagues || []).slice().sort().join('~') !== (settings.hiddenLeagues || []).slice().sort().join('~')
    || Boolean(draft.showCounts) !== Boolean(settings.showCounts)
    || (draft.timeZone || 'UTC') !== (settings.timeZone || 'UTC')
    || (draft.favoritesBoard !== false) !== (settings.favoritesBoard !== false)

  return (
    <Box sx={{ padding: '24px' }}>
      <Box sx={{ backgroundColor: 'white', padding: '24px', maxWidth: '520px' }}>
        <Typography variant="h4" sx={{ marginBottom: '20px', color: theme.palette.primary.main }}>
          Thresholds
        </Typography>

        <Box sx={{ marginBottom: '18px' }}>
          <Typography sx={{ fontSize: '13px', color: '#6E7378', marginBottom: '6px' }}>
            Value bet threshold, %
          </Typography>
          <TextField type="number" size="small" value={draft.minYield} onChange={update('minYield')} inputProps={{ step: 0.5, min: 0 }} sx={fieldSx} />
          <Typography sx={{ fontSize: '12px', color: '#9A9EA3', marginTop: '4px' }}>
            A leg is a value bet when the soft price beats the sharp one by at least this much.
          </Typography>
        </Box>

        <Box sx={{ marginBottom: '24px' }}>
          <Typography sx={{ fontSize: '13px', color: '#6E7378', marginBottom: '6px' }}>
            Arb threshold, %
          </Typography>
          <TextField type="number" size="small" value={draft.minArbProfit} onChange={update('minArbProfit')} inputProps={{ step: 0.1, min: 0 }} sx={fieldSx} />
          <Typography sx={{ fontSize: '12px', color: '#9A9EA3', marginTop: '4px' }}>
            Guaranteed profit, as a share of the total stake, before a pair counts as an arb.
          </Typography>
        </Box>

        <Typography variant="h4" sx={{ marginBottom: '6px', color: theme.palette.primary.main }}>
          Sports and leagues
        </Typography>
        <Typography sx={{ fontSize: '12px', color: '#9A9EA3', marginBottom: '10px' }}>
          Unticked entries disappear from every menu. The list is whatever the feed is carrying right now.
        </Typography>

        <Box sx={{ border: '1px solid #E4E4E8', marginBottom: '24px', maxHeight: '340px', overflowY: 'auto' }}>
          {knownLeagues.length === 0 && (
            <Typography sx={{ fontSize: '13px', color: '#9A9EA3', padding: '12px' }}>Waiting for the first menu load…</Typography>
          )}
          {knownLeagues.map(([sport, leagues]) => {
            const hiddenSport = (draft.hiddenSports || []).includes(sport)
            const hiddenHere = leagues.filter((entry) => (draft.hiddenLeagues || []).includes(leagueKey(sport, entry.league))).length
            const open = openSport === sport
            return (
              <Box key={sport} sx={{ borderBottom: '1px solid #F0F0F3' }}>
                <Box
                  onClick={() => setOpenSport(open ? null : sport)}
                  sx={{ display: 'flex', alignItems: 'center', padding: '4px 10px', cursor: 'pointer', '&:hover': { backgroundColor: '#F6F6F8' } }}
                >
                  <Checkbox
                    size="small"
                    checked={!hiddenSport}
                    onClick={toggleSport(sport)}
                    sx={{ color: '#9A9EA3', '&.Mui-checked': { color: theme.palette.primary.light } }}
                  />
                  <Typography sx={{ flexGrow: 1, fontSize: '14px', color: hiddenSport ? '#B4B8BC' : 'inherit' }}>
                    {sport}
                    <Box component="span" sx={{ color: '#9A9EA3', fontSize: '12px', marginLeft: '8px' }}>
                      {leagues.length - hiddenHere} / {leagues.length}
                    </Box>
                  </Typography>
                  <Box component="span" onClick={setWholeSport(sport, leagues, false)} sx={{ fontSize: '12px', color: theme.palette.primary.light, marginRight: '10px', '&:hover': { textDecoration: 'underline' } }}>all</Box>
                  <Box component="span" onClick={setWholeSport(sport, leagues, true)} sx={{ fontSize: '12px', color: '#9A9EA3', marginRight: '6px', '&:hover': { textDecoration: 'underline' } }}>none</Box>
                  <IconButton size="small">{open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}</IconButton>
                </Box>
                <Collapse in={open} timeout="auto" unmountOnExit>
                  <Box sx={{ padding: '2px 10px 10px 34px' }}>
                    {leagues.map((entry) => (
                      <FormControlLabel
                        key={entry.league}
                        control={
                          <Checkbox
                            size="small"
                            checked={!(draft.hiddenLeagues || []).includes(leagueKey(sport, entry.league))}
                            onChange={toggleLeague(sport, entry.league)}
                            sx={{ color: '#9A9EA3', '&.Mui-checked': { color: theme.palette.primary.light } }}
                          />
                        }
                        label={`${entry.league} (${entry.count})`}
                        sx={{ display: 'flex', '& .MuiFormControlLabel-label': { fontSize: '13px' } }}
                      />
                    ))}
                  </Box>
                </Collapse>
              </Box>
            )
          })}
        </Box>

        <Typography variant="h4" sx={{ marginBottom: '6px', color: theme.palette.primary.main }}>
          Display
        </Typography>
        <FormControlLabel
          control={<Switch checked={Boolean(draft.showCounts)} onChange={toggleCounts} color="secondary" />}
          label="Show the number of arbs next to each league"
          sx={{ display: 'flex', marginBottom: '4px', '& .MuiFormControlLabel-label': { fontSize: '14px' } }}
        />

        <FormControlLabel
          control={
            <Switch
              checked={draft.favoritesBoard !== false}
              onChange={() => { setDraft({ ...draft, favoritesBoard: draft.favoritesBoard === false }); setSaved(false) }}
              color="secondary"
            />
          }
          label="Fav compact v2: the starred games as cards, several to a row"
          sx={{ display: 'flex', marginBottom: '18px', '& .MuiFormControlLabel-label': { fontSize: '14px' } }}
        />

        <Box sx={{ marginBottom: '26px' }}>
          <Typography sx={{ fontSize: '13px', color: '#6E7378', marginBottom: '6px' }}>
            Time zone
          </Typography>
          <TextField
            select
            size="small"
            value={draft.timeZone === 'local' ? 'local' : 'UTC'}
            onChange={update('timeZone')}
            sx={fieldSx}
          >
            {ZONES.map((zone) => (
              <MenuItem key={zone.value} value={zone.value}>{zone.label}</MenuItem>
            ))}
          </TextField>
          <Typography sx={{ fontSize: '12px', color: '#9A9EA3', marginTop: '4px' }}>
            Which clock every kick off, price history and notification is written in. The feeds themselves
            speak UTC, so that is what the screens show unless you say otherwise.
          </Typography>
        </Box>

        {/* signing out drops the token and the cached settings, so the next person to use this browser
            starts from their own account rather than this one's */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: '14px',
          marginTop: '28px', paddingTop: '18px', borderTop: '1px solid #E4E4E8'
        }}>
          <Typography sx={{ flexGrow: 1, fontSize: '13px', color: '#6E7378' }}>
            Signed in as <Box component="span" sx={{ fontWeight: 700 }}>{username || '—'}</Box>
          </Typography>
          <Box
            component="button"
            onClick={async () => {
              await logout(marketHost)
              window.location.href = '/'
            }}
            sx={{
              border: '1px solid #D6D6DC', backgroundColor: 'white', borderRadius: '2px',
              padding: '7px 18px', fontSize: '13px', color: '#6E7378', cursor: 'pointer',
              '&:hover': { borderColor: theme.palette.secondary.main, color: theme.palette.secondary.main }
            }}
          >
            Sign out
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px', marginTop: '24px' }}>
          <Button
            onClick={handleSave}
            disabled={!dirty}
            sx={{
              height: '40px',
              padding: '0 28px',
              fontSize: '15px',
              letterSpacing: '0.06em',
              color: '#ffffff',
              backgroundColor: theme.palette.secondary.main,
              borderRadius: '2px',
              '&:hover': { backgroundColor: '#A81819' },
              '&.Mui-disabled': { backgroundColor: '#DCDCE2', color: '#9A9EA3' }
            }}
          >
            Save
          </Button>
          {saved && !dirty && (
            <Typography sx={{ fontSize: '13px', color: theme.palette.primary.light }}>Saved</Typography>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default SettingsPage
